import { useTrans } from "@/translations";

import { useMemo } from "react";
import { MapContainer, TileLayer } from "react-leaflet";

import { LatLngBounds } from "leaflet";
import "leaflet/dist/leaflet.css";

import { type BuildingListRow } from "../types";
import { MapCircleView } from "./mapCircleView";

type Props = {
  rows: BuildingListRow[];
};

const DEFAULT_CENTER: [number, number] = [51.5, -0.5];
const DEFAULT_ZOOM = 6;
const MAX_AUTO_ZOOM = 13;
const BOUNDS_PADDING = 26.4;

// free API key for up to 5mil requests
const CARTO_API_KEY = "cb1_2s0s_1_5e1831057f6eadeae5b6c788";

export const MapView: React.FC<Props> = ({ rows }) => {
  const { t } = useTrans();

  const bounds = useMemo(() => {
    const points = rows.filter(
      (row): row is BuildingListRow & { lat: number; lng: number } =>
        row.lat != null && row.lng != null,
    );

    if (!points.length) {
      return null;
    }

    return new LatLngBounds(points.map((row) => [row.lat, row.lng]));
  }, [rows]);

  return (
    <div className="relative">
      <MapContainer
        {...(bounds
          ? {
              bounds,
              boundsOptions: {
                padding: [BOUNDS_PADDING, BOUNDS_PADDING],
                maxZoom: MAX_AUTO_ZOOM,
              },
            }
          : { center: DEFAULT_CENTER, zoom: DEFAULT_ZOOM })}
        scrollWheelZoom={false}
        style={{
          height: 520,
          width: "100%",
          borderRadius: 8.25,
          overflow: "hidden",
          border: "1px solid #EEE7E7",
          background: "#EDEAE6",
        }}
      >
        <TileLayer
          url={`https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png?key=${CARTO_API_KEY}`}
          subdomains="abcd"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>, &copy; <a href="https://carto.com/attributions">CARTO</a>'
        />
        {rows.map((row) => (
          <MapCircleView key={row.terminalId} row={row} />
        ))}
      </MapContainer>

      <div
        className="border-grey2 pointer-events-none absolute bottom-[14px] left-[14px] z-[650] rounded-lg border px-[12px] py-2 text-[12px] text-[#3c3838]"
        style={{
          background: "rgba(255,255,255,0.96)",
          lineHeight: 1.9,
          boxShadow: "0 2px 8px rgba(0,0,0,0.10)",
        }}
      >
        <div className="flex items-center gap-3">
          <span
            className="h-[11px] w-[11px] flex-none rounded-full border-[2px] border-white"
            style={{
              background: "#3F9142",
              boxShadow: "0 0 0 1px #3F9142",
            }}
          />
          {t("portfolioPage:buildingMapLegendOnTrack")}
        </div>
        <div className="flex items-center gap-3">
          <span
            className="h-[11px] w-[11px] flex-none rounded-full border-[2px] border-white"
            style={{
              background: "#C98A1B",
              boxShadow: "0 0 0 1px #C98A1B",
            }}
          />
          {t("portfolioPage:buildingMapLegendNeedsAttention")}
        </div>
      </div>
    </div>
  );
};
